import { workspaceUrl } from "./components/useWorkspaceJson.ts";

/**
 * Workspace file links: a relative path in chat that names a file in the session's workspace and
 * downloads it through the session's workspace route. Kept free of JSX so `node --test` can load it.
 */

/** The workspace URL an explicit Markdown href names, or null when it is not a workspace file. */
export function workspaceFileHref(sessionId: string, href: string): string | null {
	const path = relativePath(decodeOnce(href.trim()));
	return path === null ? null : workspaceUrl(sessionId, path);
}

/** A scheme (`https:`, `mailto:`) or a Windows drive (`C:`). */
const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * The path itself when it stays inside the workspace; null for an address, a disk path (`/`, `~`,
 * a drive, a backslash), a bare anchor or query, a directory, or a `..` that climbs out.
 */
function relativePath(source: string): string | null {
	if (!source || SCHEME.test(source) || source.includes("\\") || /^[/~#?]/.test(source)) return null;
	const segments = source.split("/");
	if (segments.some((segment) => segment === "" || segment === "..")) return null;
	return source;
}

/** A model may write an href already percent-encoded; a stray `%` that encodes nothing is a literal. */
function decodeOnce(href: string): string {
	try {
		return decodeURIComponent(href);
	} catch {
		return href;
	}
}

/** Where a prose path sits in its text, and the path itself. */
export type ProsePath = { start: number; end: number; path: string };

/**
 * A relative path with a directory and a file extension: `files/report.pdf`, `out/2024/data.csv`.
 * It stands on its own: nothing path-like or URL-like touches either end, so no tail of `/app/x.pdf`
 * or `https://host/x.pdf` matches, while CJK text and punctuation may.
 */
const PROSE_PATH = /(?<![\w.\/\\~:@%+#?=&-])[\w.-]+(?:\/[\w.-]+)*\/[\w.-]*\.[A-Za-z][A-Za-z0-9]*(?![\w\/\\-])/g;

/** A first segment like `example.com` names a host, not a workspace directory. */
const DOMAIN = /^[\w-]+(?:\.[\w-]+)*\.[A-Za-z]{2,}$/;

/** The workspace paths prose names outright, which chat turns into Workspace file links. */
export function findProsePaths(text: string): ProsePath[] {
	return [...text.matchAll(PROSE_PATH)]
		.filter(([path]) => !DOMAIN.test(path.slice(0, path.indexOf("/"))) && relativePath(path) !== null)
		.map((m) => ({ start: m.index, end: m.index + m[0].length, path: m[0] }));
}
